<template>
  <product-section-card v-if="comments.length" class="recommended-menu" title-section-class="py-3 md:py-4">
    <template #title>
      <h2 class="flex items-center gap-1 font-500 text-lg md:text-xl">
        <span>{{ $t('branch_detail.review.title') }}</span>
        <span>{{ props.productComments.is_new ? $t('comment.new') : props.productComments.avg_rating }}</span>
        <svg-icon name="star-filled" class="text-yellow-50 size-5" />
        <span v-if="!props.productComments?.is_new && props.productComments?.total_count">
          ({{ formatNumberWithCommas(props.productComments.total_count) }})
        </span>
      </h2>
    </template>
    <template #title-action>
      <etg-button class="-mr-3" type="primary-text" size="small" @click="showCommentsViewer">
        {{ $t('general.view_all') }}
      </etg-button>
    </template>

    <div
      :class="funnowAiSummary ? '-mx-5 px-5 py-5 rounded md:grid md:grid-cols-[252px_1fr] md:gap-3 md:items-start' : ''"
      :style="funnowAiSummary ? aiSummaryCardStyle : undefined"
    >
      <div v-if="funnowAiSummary">
        <product-ai-summary-header class="mb-1" :subtitle="$t('ai.summary.eatigo')" :tooltip-text="$t('ai.eatigo.notice')" title-layout="vertical" />

        <p>{{ funnowAiSummary.description }}</p>
      </div>

      <review-tags-section
        v-else
        :pid="props.pid"
        class="gap-2 md:gap-2.5"
        :tag-size="appStore.isMobileScreen ? 'mini' : 'small'"
        @tag-click="onReviewTagClick"
      />

      <swipeable-horizontal-scroll-list
        :class="scrollListClass"
        :list="comments"
        id-key="id"
        :slides-per-group="slidesPerGroup"
        :list-container-class="listContainerClass"
        :item-class="scrollItemClass"
        arrow-btn-class="w-auto"
        :arrow-icon-class="['size-10 p-2', funnowAiSummary && 'bg-white']"
        arrow-icon-right-class="mr-3"
        arrow-icon-left-class="ml-3"
      >
        <template #default="{ item: comment }">
          <review-card
            v-if="comment.id"
            class="px-5 h-full"
            :comment-id="comment.id"
            :is-info-clickable="true"
            :is-useful-btn-shown="true"
            :is-useful="comment.is_like"
            :one-line-tags="true"
            review-content-class="line-clamp-2 md:line-clamp-4"
            :review-content="comment.description || ''"
            :review-create-time="comment.comment_time || ''"
            :review-rating="comment.rating || 1"
            :review-tags="comment.tags || []"
            :review-username="comment.rated_by || ''"
            :review-source="comment.source"
            :useful-count="comment.like_count"
            @comment-info-click="onReviewCardInfoClick"
          />
          <div v-else />
        </template>
      </swipeable-horizontal-scroll-list>
    </div>
  </product-section-card>

  <all-reviews-viewer
    v-model="isCommentsViewerShown"
    v-model:selectedReviewCardId="selectedReviewCardId"
    v-model:selectedReviewTagId="selectedReviewTagId"
    :pid="props.pid"
    :branchName="branch?.name || ''"
    :funnow-ai-summary="props.funnowAiSummary"
  />
</template>

<script setup lang="ts">
import type { ReviewReviewResponse, DomainReviewSummary } from '@/types/api'

import SwipeableHorizontalScrollList from '@/components/common/swipeable-horizontal-scroll-list.vue'
import ProductSectionCard from '@/components/product/product-detail/product-section-card.vue'
import ProductAiSummaryHeader from '@/components/product/product-detail/product-ai-summary-header.vue'
import ReviewTagsSection from '@/components/review/review-tags-section.vue'
import ReviewCard from '@/components/review/review-card.vue'
import AllReviewsViewer from '@/components/review/all-reviews-viewer.vue'

import { DEFAULT_REVIEW_TAG_ID } from '@/constants/commons'

import { useAppStore } from '@/store/app'

interface Props {
  productComments: ReviewReviewResponse
  pid: string | number
  funnowAiSummary?: DomainReviewSummary
}

const props = defineProps<Props>()

const appStore = useAppStore()

const { branch } = useBranchDetailContext()

const selectedReviewCardId = ref<number>()
const selectedReviewTagId = ref<number>(DEFAULT_REVIEW_TAG_ID)
const isCommentsViewerShown = ref(false)

const aiSummaryCardStyle = computed(() => ({
  background: appStore.isMobileScreen
    ? 'linear-gradient(182.77deg, #FEF6F7 2.31%, #FFFCFA 39.45%, #FFFFFF 97.69%)'
    : 'linear-gradient(119.41deg, #FEF6F7 18.03%, #FFFCFA 51.54%, #FFFFFF 81.97%)',
}))

const comments = computed(() => {
  if (!props.productComments.comments) return []

  const rawComments = props.productComments.comments

  if (props.funnowAiSummary) return rawComments

  const needsSpacer = rawComments.length > 1 && rawComments.length % 2 !== 0 && appStore.isLargeDesktopScreen
  return needsSpacer ? [...rawComments, {}] : rawComments
})

const isUseFullWidthCard = computed(() => comments.value.length === 1)

const scrollListClass = computed(() =>
  props.funnowAiSummary ? 'product-comments ai-summary-comments -mx-5 md:mx-0 min-w-0' : 'product-comments -mx-5'
)

const slidesPerGroup = computed(() => (props.funnowAiSummary ? 1 : 2))

const listContainerClass = computed(() => (props.funnowAiSummary ? 'pt-5 md:pb-4' : 'pt-4 md:pb-4'))

const scrollItemClass = computed(() => {
  if (isUseFullWidthCard.value) return 'w-full'
  return props.funnowAiSummary
    ? 'min-w-[355px] border-r border-gray-40 last:border-r-0'
    : 'min-w-300px border-r border-gray-40 last:border-r-0 md:min-w-1/2 lg:even:border-0'
})

function showCommentsViewer() {
  isCommentsViewerShown.value = true
}

function onReviewCardInfoClick(commentId?: number) {
  selectedReviewCardId.value = commentId
  showCommentsViewer()
}

function onReviewTagClick(tagId: number) {
  selectedReviewTagId.value = tagId
  showCommentsViewer()
}
</script>

<style lang="scss" scoped>
.recommended-menu {
  :deep(.product-comments:not(.ai-summary-comments)) {
    .list-arrow-left {
      background: linear-gradient(270deg, rgba(255, 255, 255, 0) 15%, #fff 81.25%);
    }
    .list-arrow-right {
      background: linear-gradient(270deg, #fff 15%, rgba(255, 255, 255, 0) 81.25%);
    }
  }

  &:not(:hover) {
    :deep(.list-arrow) {
      @apply opacity-0;
    }
  }

  :deep(.ai-summary-comments) {
    .list-arrow {
      background: transparent;
    }

    .list-arrow .svg-icon {
      box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
    }
  }
}
</style>
